import { Component, OnInit, Inject, ViewChild, ComponentFactoryResolver, ViewContainerRef } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material";

import { TranslateService } from "../../common/translate/translate.service";
import { AbstractWizardStepComponent } from "./abstract-wizard-step.component";
import { SdpIcon } from "../../common/icon/models";
import { isPromiseLike } from "app/util";

export namespace WizardDialogComponent {
	export interface Data<T> {
		component: AbstractWizardStepComponent.ConstructorFunction<T>;
		icon: SdpIcon.Icon;
		isCreate: boolean;
		data: T;
	}
}

@Component({
	templateUrl: "./wizard-dialog.component.html"
})
export class WizardDialogComponent<T> implements OnInit {
	@ViewChild("sdpWizardStep", {
		read: ViewContainerRef,
		static: true
	})
	public step!: ViewContainerRef;

	public stepReference!: AbstractWizardStepComponent<T>;

	public performingStep = false;

	public readonly icon = this.data.icon;
	public readonly isCreate = this.data.isCreate;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: WizardDialogComponent.Data<T>,
		public dialogRef: MatDialogRef<WizardDialogComponent<T>>,
		private translateService: TranslateService,
		private componentFactoryResolver: ComponentFactoryResolver,
		public dialog: MatDialog,
	) { }

	public async ngOnInit() {
		this.loadStep(this.data.component);
		this.stepReference.wizard = this;
	}

	public get title() {
		return this.translateService.translate(this.stepReference.title);
	}

	public get description() {
		return this.translateService.translate(this.stepReference.description);
	}

	public async deleteAction(deleteItem: boolean) {
		this.dialogRef.close(deleteItem);
	}

	public async goNext() {
		if (!this.performingStep) {
			this.performingStep = true;
			const next = this.stepReference.performNext();
			if (isPromiseLike(next)) {
				try {
					await next;
				} catch (err) {
					// If performNext() returned a promise and it rejected, return without loading the next step
					return;
				} finally {
					this.performingStep = false;
				}
			}
			if (this.stepReference.nextStep) {
				this.loadStep(this.stepReference.nextStep);
			}
			this.performingStep = false;
		}
	}

	public async goPrevious() {
		if (!this.performingStep) {
			this.performingStep = true;
			const previous = this.stepReference.performPrevious();
			if (isPromiseLike(previous)) {
				try {
					await previous;
				} catch (err) {
					// If performPrevious() returned a promise and it rejected, return without loading the previous step
					return;
				} finally {
					this.performingStep = false;
				}
			}
			if (this.stepReference.previousStep) {
				this.loadStep(this.stepReference.previousStep);
			}
			this.performingStep = false;
		}
	}

	public closeDialog() {
		this.dialog.closeAll();
	}

	public hasPrevious() {
		return !!this.stepReference.previousStep;
	}

	public hasNext() {
		return !!this.stepReference.nextStep;
	}

	public next() {
		let value = "sdp.label.next";
		if (!this.hasNext()) {
			if (this.isCreate) {
				value = "sdp.label.create";
			} else {
				value = "sdp.label.update";
			}
		}
		return this.translateService.translate(value);
	}

	public get isFormValid() {
		return this.stepReference.isFormValid;
	}

	public get showProgressBar() {
		if (this.stepReference) {
			return this.stepReference.showProgressBar || this.performingStep;
		}
		return this.performingStep;
	}

	private loadStep(step: AbstractWizardStepComponent.ConstructorFunction<T>) {
		const componentFactory = this.componentFactoryResolver.resolveComponentFactory<AbstractWizardStepComponent<T>>(step);

		this.step.clear();

		const componentRef = this.step.createComponent(componentFactory);
		componentRef.instance.data = this.data.data;
		this.stepReference = componentRef.instance;
		this.stepReference.wizard = this;
	}
}
